import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { DateTime } from 'luxon';
import { MealType } from './common/commonTypes';

const API_BASE_URL =
    process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_API_BASE_URL
        : process.env.REACT_APP_API_BASE_URL_LOCAL;

// MEAL API
export const analyzeMeal = async (meal: string) =>
    axios.post(`${API_BASE_URL}/api/v1/meal/analyze`, { meal });

export const saveMeal = async (confirmedMeals: MealType[]) => {
    const req = { confirmedMeals };
    return axios.post(`${API_BASE_URL}/api/v1/meal/save`, req);
};

export const deleteMeal = async (mealId: string) => {
    const req = { mealId };
    return axios.post(`${API_BASE_URL}/api/v1/meal/delete`, req);
};

export const editMeal = async (mealId: string, updates: MealType) => {
    const req = { mealId, updates };
    return axios.post(`${API_BASE_URL}/api/v1/meal/update`, req);
};

// FAVORITE MEALS API
export const createFavoriteMeal = async (favMeal: MealType) =>
    axios.post(`${API_BASE_URL}/api/v1/favorites/create`, { favMeal });

export const deleteFavoriteMeal = async (mealId: string) => {
    const req = { mealId };
    return axios.post(`${API_BASE_URL}/api/v1/favorites/delete`, req);
};

export const updateFavoriteMeal = async (favId: string, updates: MealType) => {
    const req = { favId, updates };
    return axios.post(`${API_BASE_URL}/api/v1/favorites/update`, req);
};

export const getFavoriteMeals = async (userId: string) =>
    axios.post(`${API_BASE_URL}/api/v1/favorites/list`, { userId });

// STAT API
export const getDailySummary = async (userId: string) => {
    // todo: do we need to make it better by providing only 1 param, similarly to getWeeklyAverages ???
    const startOfLocalDayUTC = DateTime.local().startOf('day').toUTC().toISO();
    const currentLocalDateUTC = DateTime.local().toUTC().toISO();

    const req = {
        userId,
        from: startOfLocalDayUTC,
        to: currentLocalDateUTC,
    };

    return axios.post(`${API_BASE_URL}/api/v1/daily-summary`, req);
};

export const getHistory = async (userId: string) => {
    const startOfDayWeekAgoUTC = DateTime.local().minus({ days: 7 }).startOf('day').toUTC().toISO();
    const endOfTodayUTC = DateTime.local().toUTC().toISO();

    const req = {
        userId,
        from: startOfDayWeekAgoUTC,
        to: endOfTodayUTC,
    };

    return axios.post(`${API_BASE_URL}/api/v1/history`, req);
};

export const getWeeklyAverages = async (userId: string) => {
    const startOfDayWeekAgoUTC = DateTime.local().minus({ days: 7 }).startOf('day').toUTC().toISO();
    const endOfYesterdayUTC = DateTime.local().minus({ days: 1 }).endOf('day').toUTC().toISO();
    const timeZone = DateTime.local().zoneName;

    const req = {
        userId,
        from: startOfDayWeekAgoUTC,
        to: endOfYesterdayUTC,
        timeZone,
    };

    return axios.post(`${API_BASE_URL}/api/v1/weekly-averages`, req);
};

// VOICE API
export const analyzeVoice = async (formData: any): Promise<{ data: { text: string } }> => {
    return axios.post(`${API_BASE_URL}/api/v1/voice/analyze`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

// PHOTO API
export const analyzePhoto = async (formData: any): Promise<{ data: MealType[] }> => {
    return axios.post(`${API_BASE_URL}/api/v1/photo/analyze`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

// BARCODE API
export const analyzeBarCode = async (barCode: string): Promise<{ data: MealType }> => {
    return axios.post(`${API_BASE_URL}/api/v1/bar-code/analyze`, { barCode });
};

// LOGIN - WE SET DEFAULT TOKEN IN loginUser FUNCTION IN APP.TSX
export const login = async (user: any) => axios.post(`${API_BASE_URL}/api/v1/login`, { user });

// SETTINGS API
export const saveSettings = async (userId: string, settings: any) => {
    const req = { userId, settings };
    return axios.post(`${API_BASE_URL}/api/v1/settings/save`, req);
};

// PAYMENTS API
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_LIVE;
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY as string);

export const createStripeCheckoutSession = async (
    userId: string,
    priceId: string,
    languageCode: string
) => {
    const req = {
        userId,
        priceId,
        cancelUrl: window.location.href, // todo: change to cancel page if we add react router
        successUrl: window.location.href, // todo: change to success page if we add react router
        languageCode,
    };

    const response = await axios.post(`${API_BASE_URL}/api/v1/create-checkout-session`, req);
    const { sessionId } = response.data;

    const stripe = await stripePromise;
    if (!stripe) {
        throw new Error('Stripe not loaded');
    }

    await stripe.redirectToCheckout({ sessionId });
};

export const getUrlToStripeUserPortal = async (
    stripeCustomerId: string,
    languageCode: string
): Promise<string> => {
    const req = {
        stripeCustomerId,
        returnUrl: window.location.href, // todo: change to success page if we add react router
        languageCode,
    };
    const response = await axios.post(`${API_BASE_URL}/api/v1/get-portal-url`, req);
    return response.data.url;
};

// CONTACT API
export const sendMessage = async (userId: string, data: any) => {
    const req = { userId, data };
    return axios.post(`${API_BASE_URL}/api/v1/contact/send-feedback`, req);
};
